import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { sanitizeText, sanitizeEmail } from './utils/sanitize';
import './App.css';
import AllProjects from './AllProjects';
import Admin from './Admin';
import Login from './Login';
import { verifySession } from './adminApi';
import { 
  FaLaptopCode, 
  FaPalette, 
  FaSun, 
  FaMoon, 
  FaDownload, 
  FaGithub, 
  FaLinkedin, 
  FaTwitter,
  FaBolt,
  FaRocket,
  FaLightbulb,
  FaBullseye,
  FaUser,
  FaShoppingCart,
  FaChartLine,
  FaComments,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaHeart,
  FaTimes,
  FaCheck,
  FaStar,
  FaChevronLeft,
  FaChevronRight,
  FaBars,
  FaHome,
  FaFolder,
  FaCode,
  FaArrowRight
} from 'react-icons/fa';
import { 
  SiReact, 
  SiPython, 
  SiJavascript, 
  SiDocker, 
  SiFigma,
  SiMysql,
  SiNodedotjs,
  SiHtml5,
  SiCss3,
  SiGit,
  SiTypescript,
  SiMongodb,
  SiExpress,
  SiVuedotjs
} from 'react-icons/si';

// Données des compétences
const skills = [
  { name: 'React', icon: SiReact, level: 95, color: '#61DAFB' },
  { name: 'Python', icon: SiPython, level: 90, color: '#3776AB' },
  { name: 'JavaScript', icon: SiJavascript, level: 92, color: '#F7DF1E' },
  { name: 'Docker', icon: SiDocker, level: 85, color: '#2496ED' },
  { name: 'Figma', icon: SiFigma, level: 80, color: '#F24E1E' },
  { name: 'SQL', icon: SiMysql, level: 85, color: '#4479A1' },
  { name: 'Node.js', icon: SiNodedotjs, level: 88, color: '#339933' },
  { name: 'HTML5', icon: SiHtml5, level: 90, color: '#E34F26' },
  { name: 'CSS3', icon: SiCss3, level: 90, color: '#1572B6' },
  { name: 'Git', icon: SiGit, level: 85, color: '#F05032' },
  { name: 'TypeScript', icon: SiTypescript, level: 85, color: '#3178C6' },
  { name: 'MongoDB', icon: SiMongodb, level: 80, color: '#47A248' },
  { name: 'Express', icon: SiExpress, level: 82, color: '#404040' },
  { name: 'Vue.js', icon: SiVuedotjs, level: 75, color: '#4FC08D' }
];

// Fonction pour générer la palette de couleurs
const generateColorPalette = (baseColor) => {
  // Convertir hex en RGB
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Créer des variations
  const lighter = `rgb(${Math.min(255, r + 30)}, ${Math.min(255, g + 30)}, ${Math.min(255, b + 30)})`;
  const darker = `rgb(${Math.max(0, r - 30)}, ${Math.max(0, g - 30)}, ${Math.max(0, b - 30)})`;
  const gradient = `linear-gradient(135deg, ${baseColor} 0%, ${darker} 100%)`;
  
  return { lighter, darker, gradient, r, g, b };
};

// Fonction pour appliquer les variables CSS (appelée de manière synchrone)
const applyThemeAndColor = (isDark, accentColor) => {
  document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  document.documentElement.style.setProperty('--accent-color', accentColor);
  
  const palette = generateColorPalette(accentColor);
  
  // Appliquer les couleurs à différents éléments
  document.documentElement.style.setProperty('--accent-gradient', palette.gradient);
  document.documentElement.style.setProperty('--accent-light', palette.lighter);
  document.documentElement.style.setProperty('--accent-dark', palette.darker);
  document.documentElement.style.setProperty('--accent-color-rgb', `${palette.r}, ${palette.g}, ${palette.b}`);
};

function HomePage() {
  const navigate = useNavigate();
  
  // Charger les préférences depuis localStorage SYNCHRONEMENT AVANT tout rendu
  const savedTheme = typeof window !== 'undefined' ? localStorage.getItem('theme') : null;
  const savedColor = typeof window !== 'undefined' ? localStorage.getItem('accentColor') : null;
  const initialDarkMode = savedTheme ? savedTheme === 'dark' : true;
  const initialAccentColor = savedColor || '#3b82f6';
  
  // APPLIQUER IMMÉDIATEMENT les variables CSS AVANT tout rendu React
  // Cette fonction est appelée de manière synchrone, avant que React ne rende quoi que ce soit
  if (typeof window !== 'undefined') {
    applyThemeAndColor(initialDarkMode, initialAccentColor);
  }
  
  const [isDarkMode, setIsDarkMode] = useState(initialDarkMode);
  const [accentColor, setAccentColor] = useState(initialAccentColor);
  const [isLoading, setIsLoading] = useState(true);
  const [currentText, setCurrentText] = useState('');
  const [isWelcomePhase, setIsWelcomePhase] = useState(true);
  const [isColorModalOpen, setIsColorModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  // Détecter mobile IMMÉDIATEMENT pour éviter les incohérences
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [currentSkillIndex, setCurrentSkillIndex] = useState(() => {
    // Initialiser selon la taille d'écran au démarrage
    return window.innerWidth <= 768 ? 1 : 4;
  });
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [contactFormStatus, setContactFormStatus] = useState({ type: null, message: '', closing: false });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [cvNotification, setCvNotification] = useState({ show: false, closing: false, message: '' });
  // Notifications pour la section Hero
  const [githubNotificationHero, setGithubNotificationHero] = useState({ show: false, closing: false, message: '' });
  const [linkedinNotificationHero, setLinkedinNotificationHero] = useState({ show: false, closing: false, message: '' });
  const [twitterNotificationHero, setTwitterNotificationHero] = useState({ show: false, closing: false, message: '' });
  // Notifications pour la section Contact
  const [githubNotificationContact, setGithubNotificationContact] = useState({ show: false, closing: false, message: '' });
  const [linkedinNotificationContact, setLinkedinNotificationContact] = useState({ show: false, closing: false, message: '' });
  const [twitterNotificationContact, setTwitterNotificationContact] = useState({ show: false, closing: false, message: '' });
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [isPageContentVisible, setIsPageContentVisible] = useState(false);

  // Détecter si on est sur mobile et réinitialiser l'index si nécessaire
  useEffect(() => {
    const checkMobile = () => {
      const nowMobile = window.innerWidth <= 768;
      const wasMobile = isMobile;
      
      if (nowMobile !== wasMobile) {
        setIsMobile(nowMobile);
        // Réinitialiser l'index quand on change de mode
        setCurrentSkillIndex(nowMobile ? 1 : 4);
      }
    };
    
    // Initialiser au montage
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile]);

  // Gérer le scroll automatique vers la section contact via hash (#contact)
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#contact') {
        const scrollToContact = () => {
          const contactSection = document.getElementById('contact');
          if (contactSection) {
            const headerOffset = 80;
            const elementPosition = contactSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
            return true;
          }
          return false;
        };

        // Attendre un peu que le DOM soit rendu
        setTimeout(() => {
          if (!scrollToContact()) {
            // Réessayer avec des délais progressifs
            const tryScroll = (attempt = 1) => {
              setTimeout(() => {
                if (scrollToContact()) {
                  return;
                }
                if (attempt < 5) {
                  tryScroll(attempt + 1);
                }
              }, attempt === 1 ? 300 : attempt * 200);
            };
            tryScroll();
          }
        }, 100);
      }
    };

    // Vérifier le hash au montage et quand il change
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Initialiser EmailJS avec la Public Key
  useEffect(() => {
    emailjs.init('2O3zbLSKoAosbxrPp');
  }, []);

  // Fonction pour gérer l'envoi du formulaire de contact via EmailJS
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingContact(true);
    setContactFormStatus({ type: null, message: '' });

    try {
      // Récupérer les valeurs du formulaire
      const formData = new FormData(e.target);
      const name = formData.get('name')?.toString().trim() || '';
      const email = formData.get('email')?.toString().trim() || '';
      const subject = formData.get('subject')?.toString().trim() || '';
      const message = formData.get('message')?.toString().trim() || '';
      
      // Validation des champs
      if (!name || !email || !subject || !message) {
        throw new Error('Veuillez remplir tous les champs du formulaire.');
      }
      
      const currentTime = new Date().toLocaleString('fr-FR', { 
        dateStyle: 'full', 
        timeStyle: 'short' 
      });
      
      console.log('Tentative d\'envoi d\'email avec les paramètres:', {
        from_name: name,
        from_email: email,
        subject: subject,
        message_length: message.length,
        time: currentTime
      });
      
      // Préparer les paramètres pour le template EmailJS
      // Le template utilise : {{from_name}}, {{from_email}}, {{subject}}, {{message}}, {{time}}
      const templateParams = {
        from_name: sanitizeText(name, { maxLength: 80 }),
        from_email: sanitizeEmail(email),
        subject: sanitizeText(subject, { maxLength: 120 }),
        message: sanitizeText(message, { maxLength: 2000 }),
        time: currentTime
      };
      
      // ===== ÉTAPE 1 : Envoyer l'email au propriétaire =====
      console.log('📤 ÉTAPE 1 : Envoi de l\'email au propriétaire...');
      console.log('Paramètres:', {
        serviceId: 'service_qoc2obg',
        templateId: 'template_g2d8qbk',
        params: templateParams
      });
      
      const result = await emailjs.send(
        'service_qoc2obg',      // Service ID
        'template_g2d8qbk',     // Template ID (email au propriétaire)
        templateParams,          // Paramètres du template
        '2O3zbLSKoAosbxrPp'     // Public Key
      );
      
      console.log('✅ ÉTAPE 1 RÉUSSIE : Email au propriétaire envoyé avec succès');
      console.log('  Status:', result.status);
      console.log('  Status Text:', result.text);
      console.log('  Response:', JSON.stringify(result, null, 2));

      // Petit délai pour éviter les problèmes de rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));

      // ===== ÉTAPE 2 : Envoyer l'email de confirmation automatique à l'utilisateur =====
      console.log('📤 ÉTAPE 2 : Envoi de l\'email de confirmation automatique...');
      
      // Validation des paramètres avant l'envoi
      if (!email || !email.includes('@')) {
        throw new Error('Email invalide pour l\'envoi de confirmation');
      }
      
      // Paramètres pour l'email de confirmation à l'utilisateur
      // IMPORTANT : Dans EmailJS, le template doit avoir "To Email" configuré sur {{from_email}}
      const confirmationTemplateParams = {
        from_name: sanitizeText(name, { maxLength: 80 }),
        from_email: sanitizeEmail(email),
        website_link: window.location.origin || 'https://www.mrtchoukdev.com'
      };
      
      console.log('📧 Paramètres de confirmation:', {
        serviceId: 'service_qoc2obg',
        templateId: 'template_4g4wtt6',
        toEmail: email,
        toName: name,
        params: confirmationTemplateParams
      });
      
      try {
        // Envoyer l'email de confirmation
        // Template ID : template_4g4wtt6 (Auto-Reply)
        const confirmationResult = await emailjs.send(
          'service_qoc2obg',           // Même Service ID
          'template_4g4wtt6',          // Template ID de confirmation (Auto-Reply)
          confirmationTemplateParams,   // Paramètres du template
          '2O3zbLSKoAosbxrPp'           // Public Key
        );
        
        console.log('✅ ÉTAPE 2 RÉUSSIE : Email de confirmation envoyé avec succès');
        console.log('  Status:', confirmationResult.status);
        console.log('  Status Text:', confirmationResult.text);
        console.log('  Response:', JSON.stringify(confirmationResult, null, 2));
        console.log('🎉 Les deux emails ont été envoyés avec succès !');
        
      } catch (confirmationError) {
        // Logs détaillés de l'erreur
        console.error('❌ ÉTAPE 2 ÉCHOUÉE : Erreur lors de l\'envoi de l\'email de confirmation');
        console.error('  Type d\'erreur:', typeof confirmationError);
        console.error('  Erreur complète:', confirmationError);
        console.error('  Error.text:', confirmationError.text);
        console.error('  Error.message:', confirmationError.message);
        console.error('  Error.status:', confirmationError.status);
        console.error('  Error.statusText:', confirmationError.statusText);
        
        if (confirmationError.text) {
          if (confirmationError.text.includes('Invalid template ID')) {
            console.error('  ⚠️ PROBLÈME : Template ID invalide. Vérifiez que template_4g4wtt6 existe et est publié.');
          } else if (confirmationError.text.includes('Invalid service ID')) {
            console.error('  ⚠️ PROBLÈME : Service ID invalide.');
          } else if (confirmationError.text.includes('Forbidden')) {
            console.error('  ⚠️ PROBLÈME : Accès refusé. Vérifiez vos clés API EmailJS.');
          } else {
            console.error('  ⚠️ PROBLÈME :', confirmationError.text);
          }
        }
        
        // Ne pas bloquer si l'email de confirmation échoue
        // L'email principal a déjà été envoyé avec succès
        console.warn('⚠️ RÉSUMÉ : L\'email principal a bien été envoyé, mais la confirmation a échoué.');
      }

      setContactFormStatus({ 
        type: 'success', 
        message: 'Message envoyé avec succès ! Vous allez recevoir un email de confirmation. Je vous répondrai dans les plus brefs délais.',
        closing: false
      });
      
      // Scroll vers la notification pour qu'elle soit visible
      setTimeout(() => {
        const statusElement = document.querySelector('.form-status.success');
        if (statusElement) {
          statusElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      // Faire disparaître la notification automatiquement après 5 secondes avec animation
      setTimeout(() => {
        setContactFormStatus(prev => ({ ...prev, closing: true }));
        setTimeout(() => {
          setContactFormStatus({ type: null, message: '', closing: false });
        }, 600);
      }, 5000);
      
      e.target.reset(); // Réinitialiser le formulaire
    } catch (error) {
      console.error('❌ Erreur EmailJS complète:', error);
      console.error('Type d\'erreur:', typeof error);
      console.error('Code d\'erreur:', error.text || error.message || error);
      console.error('Status:', error.status);
      console.error('Stack:', error.stack);
      
      // Message d'erreur plus détaillé pour le débogage
      let errorMessage = 'Une erreur est survenue. ';
      
      if (error.text) {
        // Erreurs spécifiques EmailJS
        if (error.text.includes('Invalid service ID')) {
          errorMessage += 'Service ID invalide. Vérifiez votre configuration EmailJS.';
        } else if (error.text.includes('Invalid template ID')) {
          errorMessage += 'Template ID invalide. Vérifiez votre configuration EmailJS.';
        } else if (error.text.includes('Invalid public key')) {
          errorMessage += 'Clé publique invalide. Vérifiez votre configuration EmailJS.';
        } else if (error.text.includes('Forbidden')) {
          errorMessage += 'Accès refusé. Vérifiez vos clés API EmailJS.';
        } else {
          errorMessage += `Erreur EmailJS : ${error.text}`;
        }
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Veuillez réessayer ou me contacter directement par email.';
      }
      
      setContactFormStatus({ 
        type: 'error', 
        message: errorMessage,
        closing: false
      });
      
      // Garder le message d'erreur visible plus longtemps avec animation
      setTimeout(() => {
        setContactFormStatus(prev => prev.type === 'error' ? { ...prev, closing: true } : prev);
        setTimeout(() => {
          setContactFormStatus(prev => prev.type === 'error' ? { type: null, message: '', closing: false } : prev);
        }, 600);
      }, 8000);
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
            setTimeout(() => {
              setIsLoading(false);
              // Déclencher l'animation du header après un court délai
              setTimeout(() => {
                setIsHeaderVisible(true);
                // Déclencher l'animation du contenu après le header
                setTimeout(() => {
                  setIsPageContentVisible(true);
                }, 600);
              }, 200);
            }, 300);
          return 100;
        }
        return prev + 2;
      });
    }, 40); // Augmente de 2% toutes les 40ms = 2 secondes pour atteindre 100%

    return () => clearInterval(progressInterval);
  }, []);

  // Si le loading screen est déjà passé (par exemple au refresh), afficher le header et le contenu directement
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setIsHeaderVisible(true);
        setTimeout(() => {
          setIsPageContentVisible(true);
        }, 600);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading) {
      // Réinitialiser le texte quand on change de phase
      if (isWelcomePhase) {
        setCurrentText('');
      }
      
      const welcomeTexts = [
        "// Portfolio Mrtchouk",
        "// Développeur Full Stack"
      ];
      
      const techTexts = [
        "const developer = \"Développeur Full Stack\";",
        "const specialization = \"Développeur Web\";",
        "const backend = \"Développeur Backend\";",
        "",
        "const technologies = [",
        "  \"React\", \"JavaScript\", \"Node.js\",",
        "  \"Python\", \"TypeScript\", \"MongoDB\",",
        "  \"Docker\", \"Git\", \"Figma\"",
        "];"
      ];

      const texts = isWelcomePhase ? welcomeTexts : techTexts;
      let textIndex = 0;
      let charIndex = 0;

      const typeInterval = setInterval(() => {
        if (textIndex < texts.length) {
          const currentLine = texts[textIndex];
          
          if (charIndex < currentLine.length) {
            setCurrentText(prev => {
              // Éviter les répétitions en vérifiant la longueur
              const expectedLength = texts.slice(0, textIndex).join('\n').length + textIndex + charIndex + 1;
              if (prev.length >= expectedLength) {
                return prev; // Ne pas ajouter si déjà présent
              }
              return prev + currentLine[charIndex];
            });
            charIndex++;
          } else {
            // Fin de la ligne, passer à la suivante
            setCurrentText(prev => {
              if (prev.endsWith('\n')) {
                return prev; // Déjà un saut de ligne
              }
              return prev + '\n';
            });
            textIndex++;
            charIndex = 0;
          }
        } else {
          // Fin de tous les textes
          clearInterval(typeInterval);
          
          if (isWelcomePhase) {
            // Attendre 2 secondes puis passer aux technologies
            setTimeout(() => {
              setIsWelcomePhase(false);
              setCurrentText(''); // Réinitialiser pour la phase tech
            }, 2000);
          }
        }
      }, 100);

      return () => {
        clearInterval(typeInterval);
      };
    }
  }, [isLoading, isWelcomePhase]);

  // Reprendre l'auto-play après 5 secondes d'inactivité
  useEffect(() => {
    if (isAutoPlaying) return;
    
    const timeout = setTimeout(() => {
      setIsAutoPlaying(true);
    }, 5000); // Reprend après 5 secondes

    return () => clearTimeout(timeout);
  }, [isAutoPlaying, currentSkillIndex]);

  // Auto-rotation du carrousel - BOUCLE INFINIE FLUIDE MOBILE
  useEffect(() => {
    if (!isAutoPlaying || isTransitioning) return;
    
    const interval = setInterval(() => {
      setCurrentSkillIndex((prevIndex) => {
        if (isMobile) {
          // ===== MOBILE : Boucle infinie fluide =====
          // Index va de 1 à skills.length (1 à 14)
          // Quand on atteint la fin, on saute directement (sans transition) au début
          const startIndex = 1;
          const maxRealIndex = skills.length;
          const nextIndex = prevIndex + 1;
          
          if (nextIndex > maxRealIndex) {
            // Fin atteinte : sauter invisiblement au début
            setIsTransitioning(false); // Pas de transition pour le saut
            setTimeout(() => {
              setCurrentSkillIndex(startIndex);
              setIsTransitioning(true); // Réactiver transitions pour les prochaines
              setTimeout(() => setIsTransitioning(false), 100);
            }, 50);
            return maxRealIndex; // Retourner l'index max pour éviter l'index 15
          }
          
          setIsTransitioning(true);
          setTimeout(() => setIsTransitioning(false), 800);
          return nextIndex;
        } else {
          // ===== DESKTOP : Logique existante (CONSERVÉE) =====
          const startIndex = 4;
          const maxRealIndex = startIndex + (skills.length - 4);
          const nextIndex = prevIndex + 1;
          
          if (nextIndex > maxRealIndex) {
            setIsTransitioning(true);
            setTimeout(() => {
              setIsTransitioning(false);
              setTimeout(() => {
                setCurrentSkillIndex(startIndex);
              }, 50);
            }, 800);
            return maxRealIndex;
          }
          
          setIsTransitioning(true);
          setTimeout(() => setIsTransitioning(false), 800);
          return nextIndex;
        }
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, isTransitioning, isMobile]);

  // S'assurer que l'index reste dans les limites valides
  useEffect(() => {
    if (isMobile) {
      // Mobile : index de 1 à skills.length
      if (currentSkillIndex < 1 || currentSkillIndex > skills.length) {
        setCurrentSkillIndex(1);
      }
    } else {
      // Desktop : index de 4 à 4 + (skills.length - 4)
      const startIndex = 4;
      const maxIndex = startIndex + (skills.length - 4);
      if (currentSkillIndex < startIndex || currentSkillIndex > maxIndex) {
        setCurrentSkillIndex(startIndex);
      }
    }
  }, [isMobile, currentSkillIndex]);

  // Calculer le transform - CALCUL PRÉCIS POUR REMPLACEMENT EXACT
  const getMobileTransform = () => {
    // DESKTOP : Logique existante (CONSERVÉE)
    if (!isMobile) {
      // Ajustement pour avancer la première carte de 50px
      const desktopOffset = 50;
      return `translateX(calc(${desktopOffset}px - ${currentSkillIndex} * (100% / 4)))`;
    }
    
    // ===== MOBILE : Calcul précis pour que chaque carte prenne exactement la place de la précédente =====
    const viewportWidth = window.innerWidth;
    
    // Déterminer cardWidth selon le breakpoint (doit correspondre EXACTEMENT au CSS)
    let cardWidth;
    if (viewportWidth <= 480) {
      // Ultra mobile : width = calc(100vw - 88px) selon CSS
      cardWidth = viewportWidth - 88;
    } else {
      // Mobile standard : width = calc(100vw - 120px) selon CSS
      cardWidth = viewportWidth - 120;
    }
    
    // IMPORTANT : Le gap CSS (1rem) est déjà géré par le navigateur dans le layout flexbox
    // On ne doit PAS l'ajouter dans le calcul du transform, car il est appliqué automatiquement
    // La distance entre les cartes est donc uniquement cardWidth (le gap est géré par CSS)
    
    // AJUSTEMENT : Position parfaite trouvée par l'utilisateur pour la première carte (2220px)
    const adjustmentOffset = 2220;
    
    // La première carte doit commencer exactement à cette position
    const firstCardFinalPosition = adjustmentOffset;
    
    // LEÇON APPRISE : Dans Flexbox avec gap CSS, le navigateur applique automatiquement le gap
    // Donc la distance entre le début de deux cartes = cardWidth uniquement (le gap est déjà dans le layout
    // Structure DOM : [clone index 0] [carte 1] [carte 2] ... [carte 14] [clone index 15]
    // Le clone (index 0) prend cardWidth d'espace (le gap après lui est géré par CSS)
    // Pour que la carte index 1 commence à firstCardFinalPosition :
    // trackOffset = firstCardFinalPosition - cardWidth
    const trackOffsetForFirstCard = firstCardFinalPosition - cardWidth;
    
    // Chaque carte suivante se décale de EXACTEMENT cardWidth vers la gauche
    // Le gap CSS s'applique automatiquement entre les cartes, pas besoin de l'ajouter dans le transform
    // offset = position première carte - (nombre de cartes avant) * cardWidth
    const offset = trackOffsetForFirstCard - ((currentSkillIndex - 1) * cardWidth);
    
    return `translateX(${offset}px)`;
  };

  const handlePrev = () => {
    setIsAutoPlaying(false);
    setIsTransitioning(true);
    
    if (isMobile) {
      // ===== MOBILE : Boucle infinie fluide =====
      setCurrentSkillIndex((prevIndex) => {
        const startIndex = 1;
        const maxRealIndex = skills.length;
        const newIndex = prevIndex - 1;
        
        // Si on va avant le début, sauter invisiblement à la fin
        if (newIndex < startIndex) {
          setIsTransitioning(false); // Pas de transition pour le saut
          setTimeout(() => {
            setCurrentSkillIndex(maxRealIndex);
            setIsTransitioning(true);
            setTimeout(() => setIsTransitioning(false), 100);
          }, 50);
          return startIndex; // Ne pas aller à l'index 0 (clone)
        }
        
        setTimeout(() => setIsTransitioning(false), 800);
        return newIndex;
      });
    } else {
      // ===== DESKTOP : Logique existante (CONSERVÉE) =====
      setCurrentSkillIndex((prevIndex) => {
        const startIndex = 4;
        const newIndex = prevIndex - 1;
        
        if (newIndex < startIndex) {
          const maxIndex = startIndex + (skills.length - 4);
          setTimeout(() => {
            setIsTransitioning(false);
            setCurrentSkillIndex(maxIndex);
          }, 800);
        } else {
          setTimeout(() => setIsTransitioning(false), 800);
        }
        
        return newIndex;
      });
    }
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setIsTransitioning(true);
    
    if (isMobile) {
      // ===== MOBILE : Boucle infinie fluide =====
      setCurrentSkillIndex((prevIndex) => {
        const startIndex = 1;
        const maxRealIndex = skills.length;
        const newIndex = prevIndex + 1;
        
        // Si on dépasse la fin, sauter invisiblement au début
        if (newIndex > maxRealIndex) {
          setIsTransitioning(false); // Pas de transition pour le saut
          setTimeout(() => {
            setCurrentSkillIndex(startIndex);
            setIsTransitioning(true);
            setTimeout(() => setIsTransitioning(false), 100);
          }, 50);
          return maxRealIndex; // Ne pas aller à l'index 15 (clone)
        }
        
        setTimeout(() => setIsTransitioning(false), 800);
        return newIndex;
      });
    } else {
      // ===== DESKTOP : Logique existante (CONSERVÉE) =====
      setCurrentSkillIndex((prevIndex) => {
        const startIndex = 4;
        const maxRealIndex = startIndex + (skills.length - 4);
        const newIndex = prevIndex + 1;
        
        if (newIndex > maxRealIndex) {
          setTimeout(() => {
            setIsTransitioning(false);
            setTimeout(() => {
              setCurrentSkillIndex(startIndex);
            }, 50);
          }, 800);
          return maxRealIndex;
        }
        
        setTimeout(() => setIsTransitioning(false), 800);
        return newIndex;
      });
    }
  };

  useEffect(() => {
    // Appliquer le thème et la couleur à chaque changement
    applyThemeAndColor(isDarkMode, accentColor);
    
    // Sauvegarder dans localStorage
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('accentColor', accentColor);
  }, [isDarkMode, accentColor]);

  const colors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', 
    '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
    '#f97316', '#22c55e', '#a855f7', '#d946ef'
  ];

  const getColoredText = (text) => {
    return text
      // Commentaires (//)
      .replace(/(\/\/.*)/g, '<span class="code-comment">$1</span>')
      // Mots-clés JavaScript
      .replace(/\b(const|let|var)\b/g, '<span class="code-keyword">$1</span>')
      // Variables
      .replace(/\b(developer|specialization|backend|technologies)\b/g, '<span class="code-variable">$1</span>')
      // Opérateurs
      .replace(/([=,\x5B\x5D]+)/g, '<span class="code-operator">$1</span>')
      // Chaînes de caractères (en vert comme avant)
      .replace(/"([^"]*)"/g, '<span class="code-string">"$1"</span>');
  };

  const closeModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setIsColorModalOpen(false);
      setIsModalClosing(false);
    }, 300);
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-background">
          <div className="loading-orb orb-1"></div>
          <div className="loading-orb orb-2"></div>
          <div className="loading-orb orb-3"></div>
        </div>
        <div className="loading-content">
          <div className="loading-logo-container">
            <div className="loading-logo-ring"></div>
          <div className="loading-logo">
              <FaLaptopCode />
          </div>
          </div>
          <div className="loading-text">
            <h1 className="loading-title">
              <span className="loading-name">Mrtchouk</span>
              <span className="loading-year">{new Date().getFullYear()}</span>
            </h1>
            <p className="loading-subtitle">Portfolio Développeur Full Stack</p>
          </div>
          <div className="loading-bar-container">
          <div className="loading-bar">
              <div className="loading-progress" style={{ width: `${loadingProgress}%` }}></div>
            </div>
            <div className="loading-percentage">{loadingProgress}%</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Header */}
      <header className={`header ${isHeaderVisible ? 'header-visible' : ''}`}>
        <div className="header-container">
        <div className={`logo ${isHeaderVisible ? 'logo-visible' : ''}`}>
          <span className="logo-text">Mrtchouk</span>
          <span className="logo-year">{new Date().getFullYear()}</span>
        </div>

          <div className={`header-controls ${isHeaderVisible ? 'controls-visible' : ''}`}>
            {/* Color Picker Button */}
            <button
              className="control-btn color-picker-btn"
              onClick={() => setIsColorModalOpen(!isColorModalOpen)}
              title="Changer les couleurs"
            >
              <FaPalette />
            </button>

            {/* Theme Toggle */}
            <button
              className="control-btn theme-toggle"
              onClick={() => setIsDarkMode(!isDarkMode)}
              title={isDarkMode ? 'Passer au mode clair' : 'Passer au mode sombre'}
            >
              {isDarkMode ? <FaSun /> : <FaMoon />}
            </button>

            {/* Mobile Menu Toggle */}
            <button
              className="control-btn mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              title="Menu"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </header>

      {/* Overlay et menu mobile - Complètement détaché du header, au premier plan */}
      <div 
        className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-label="Fermer le menu"
      />

      <nav className={`nav ${isMobileMenuOpen ? 'nav-open' : ''}`}>
        <button 
          className="mobile-menu-close"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Fermer le menu"
        >
          <FaTimes />
        </button>
        <div className="mobile-menu-header">
          <div className="mobile-menu-logo">
            <span className="mobile-logo-text">Mrtchouk</span>
            <span className="mobile-logo-year">{new Date().getFullYear()}</span>
          </div>
        </div>
        <div className="mobile-menu-content">
          <a href="#home" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon"><FaHome /></span>
            <span className="nav-text">Accueil</span>
          </a>
          <a href="#about" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon"><FaUser /></span>
            <span className="nav-text">À propos</span>
          </a>
          <a href="#skills" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon"><FaCode /></span>
            <span className="nav-text">Compétences</span>
          </a>
          <a href="#projects" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon"><FaFolder /></span>
            <span className="nav-text">Projets</span>
          </a>
          <a href="#contact" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon"><FaEnvelope /></span>
            <span className="nav-text">Contact</span>
          </a>
          {/* Pas de lien Admin dans la navigation publique : l'accès se fait
              directement par /login. Un lien masqué visuellement reste présent
              dans le DOM, donc cliquable et découvrable. */}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className={`hero ${isPageContentVisible ? 'section-visible' : ''}`}>
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>

        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-text"><FaStar /> Portfolio {new Date().getFullYear()}</span>
            </div>

            <h1 className="hero-title">
              <span className="title-line">Bonjour, je suis</span>
              <span className="title-name">Mrtchouk</span>
            </h1>

            <p className="hero-description">
              Je crée des expériences numériques exceptionnelles avec les dernières technologies. 
              Spécialisé dans le développement web moderne, les applications backend robustes 
              et l'optimisation des performances.
            </p>

            <div className="hero-actions">
              <button 
                className="btn btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.currentTarget.blur(); // Désélectionner le bouton
                  // Déclencher le loading avant la navigation
                  localStorage.setItem('loadingMessage', 'Vous vous dirigez actuellement vers la page projet');
                  navigate('/projects');
                }}
              >
                <span>Voir mes projets</span>
              </button>

              <button 
                className="btn btn-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  e.currentTarget.blur(); // Désélectionner le bouton
                  setCvNotification({ 
                    show: true, 
                    closing: false,
                    message: 'Le CV n\'est pas encore disponible. Il sera disponible prochainement. Pour l\'instant, veuillez me contacter par email.' 
                  });
                  // Faire disparaître la notification automatiquement après 8 secondes avec animation
                  setTimeout(() => {
                    setCvNotification(prev => ({ ...prev, closing: true }));
                    setTimeout(() => {
                      setCvNotification({ show: false, closing: false, message: '' });
                    }, 600);
                  }, 8000);
                }}
              >
                <span><FaDownload /> Télécharger CV</span>
              </button>
            </div>
            
            {/* Notification pour le CV */}
            {cvNotification.show && (
              <div className={`form-status warning cv-notification ${cvNotification.closing ? 'closing' : ''}`} style={{ 
                marginTop: '1.5rem'
              }}>
                <div className="form-status-content">
                  <div className="status-icon" style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
                  }}>
                    <FaEnvelope />
                  </div>
                  <div className="status-message">
                    {cvNotification.message}
                  </div>
                </div>
              </div>
            )}

            <div className="hero-social">
              <button
                className="social-link"
                onClick={(e) => {
                  e.preventDefault();
                  e.currentTarget.blur(); // Désélectionner le bouton
                  setGithubNotificationHero({ 
                    show: true, 
                    closing: false,
                    message: 'Le GitHub n\'est pas encore prêt, veuillez patienter.' 
                  });
                  setTimeout(() => {
                    setGithubNotificationHero(prev => ({ ...prev, closing: true }));
                    setTimeout(() => {
                      setGithubNotificationHero({ show: false, closing: false, message: '' });
                    }, 600);
                  }, 8000);
                }}
              >
                <FaGithub /> GitHub
              </button>
              
              <button
                className="social-link"
                onClick={(e) => {
                  e.preventDefault();
                  e.currentTarget.blur(); // Désélectionner le bouton
                  setLinkedinNotificationHero({ 
                    show: true, 
                    closing: false,
                    message: 'LinkedIn n\'est pas encore prêt, veuillez patienter, il sera disponible prochainement.' 
                  });
                  setTimeout(() => {
                    setLinkedinNotificationHero(prev => ({ ...prev, closing: true }));
                    setTimeout(() => {
                      setLinkedinNotificationHero({ show: false, closing: false, message: '' });
                    }, 600);
                  }, 8000);
                }}
              >
                <FaLinkedin /> LinkedIn
              </button>
              
              <button
                className="social-link"
                onClick={(e) => {
                  e.preventDefault();
                  e.currentTarget.blur(); // Désélectionner le bouton
                  setTwitterNotificationHero({ 
                    show: true, 
                    closing: false,
                    message: 'Le compte Twitter est en cours de préparation, veuillez patienter, merci.' 
                  });
                  setTimeout(() => {
                    setTwitterNotificationHero(prev => ({ ...prev, closing: true }));
                    setTimeout(() => {
                      setTwitterNotificationHero({ show: false, closing: false, message: '' });
                    }, 600);
                  }, 8000);
                }}
              >
                <FaTwitter /> Twitter
              </button>
            </div>
            
            {/* Notification pour GitHub - Hero */}
            {githubNotificationHero.show && (
              <div className={`form-status warning cv-notification ${githubNotificationHero.closing ? 'closing' : ''}`} style={{ 
                marginTop: '1.5rem'
              }}>
                <div className="form-status-content">
                  <div className="status-icon" style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
                  }}>
                    <FaGithub />
                  </div>
                  <div className="status-message">
                    {githubNotificationHero.message}
                  </div>
                </div>
              </div>
            )}

            {/* Notification pour LinkedIn - Hero */}
            {linkedinNotificationHero.show && (
              <div className={`form-status warning cv-notification ${linkedinNotificationHero.closing ? 'closing' : ''}`} style={{ 
                marginTop: '1.5rem'
              }}>
                <div className="form-status-content">
                  <div className="status-icon" style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
                  }}>
                    <FaLinkedin />
                  </div>
                  <div className="status-message">
                    {linkedinNotificationHero.message}
                  </div>
                </div>
              </div>
            )}

            {/* Notification pour Twitter - Hero */}
            {twitterNotificationHero.show && (
              <div className={`form-status warning cv-notification ${twitterNotificationHero.closing ? 'closing' : ''}`} style={{ 
                marginTop: '1.5rem'
              }}>
                <div className="form-status-content">
                  <div className="status-icon" style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
                  }}>
                    <FaTwitter />
                  </div>
                  <div className="status-message">
                    {twitterNotificationHero.message}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="hero-visual">
            <div className="code-window">
              <div className="window-header">
                <div className="window-controls">
                  <span className="control red"></span>
                  <span className="control yellow"></span>
                  <span className="control green"></span>
                </div>
                <div className="window-title">portfolio.js</div>
              </div>
              <div className="code-content">
                <pre 
                  className="code-text"
                  dangerouslySetInnerHTML={{
                    __html: getColoredText(currentText) + '<span class="code-cursor">|</span>'
                  }}
                />
              </div>
            </div>


          </div>
        </div>

      </section>

      {/* About Section */}
      <section id="about" className={`about ${isPageContentVisible ? 'section-visible' : ''}`}>
        {/* Icônes flottantes dans la section About */}
        <div className="about-floating-elements">
          <div className="floating-icon icon-1"><FaBolt /></div>
          <div className="floating-icon icon-2"><FaRocket /></div>
          <div className="floating-icon icon-3"><FaLightbulb /></div>
          <div className="floating-icon icon-4"><FaBullseye /></div>
        </div>
        <div className="about-container">
          <div className="about-content">
            <div className="section-badge">
              <span>À propos</span>
            </div>

            <h2 className="section-title">
              Passionné par l'innovation technologique
            </h2>

            <p className="about-description">
              Développeur full stack avec une expertise approfondie dans les technologies modernes. 
              Je me spécialise dans la création d'applications web performantes et d'expériences 
              utilisateur exceptionnelles. Mon approche combine créativité, rigueur technique 
              et attention aux détails pour livrer des solutions sur mesure.
            </p>

            <div className="about-stats">
              <div className="stat-item">
                <div className="stat-number">50+</div>
                <div className="stat-label">Projets réalisés</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">3+</div>
                <div className="stat-label">Années d'expérience</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">15+</div>
                <div className="stat-label">Technologies maîtrisées</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">100%</div>
                <div className="stat-label">Satisfaction client</div>
              </div>
            </div>
          </div>

          <div className="about-visual">
            <div className="about-card">
              <div className="card-header">
                <div className="card-avatar">
                  <div className="avatar-image"><FaUser /></div>
                  <div className="avatar-status"></div>
                </div>
                <div className="card-info">
                  <h3>Développeur Full Stack</h3>
                  <p>Disponible pour nouveaux projets</p>
                </div>
              </div>

              <div className="card-content">
                <div className="skill-bar">
                  <div className="skill-label">React & Frontend</div>
                  <div className="skill-progress">
                    <div className="skill-fill" style={{ width: "95%" }}></div>
                  </div>
                </div>

                <div className="skill-bar">
                  <div className="skill-label">Python & Backend</div>
                  <div className="skill-progress">
                    <div className="skill-fill" style={{ width: "90%" }}></div>
                  </div>
                </div>

                <div className="skill-bar">
                  <div className="skill-label">LUA & Scripting</div>
                  <div className="skill-progress">
                    <div className="skill-fill" style={{ width: "85%" }}></div>
                  </div>
                </div>

                <div className="skill-bar">
                  <div className="skill-label">DevOps & Docker</div>
                  <div className="skill-progress">
                    <div className="skill-fill" style={{ width: "80%" }}></div>
                  </div>
                </div>
              </div>

              <div className="card-footer">
                <button 
                  className="contact-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.currentTarget.blur(); // Désélectionner le bouton
                    // Scroll vers la section contact
                    const contactSection = document.getElementById('contact');
                    if (contactSection) {
                      const headerOffset = 80;
                      const elementPosition = contactSection.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                      window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                      });
                    } else {
                      // Si la section n'existe pas encore, utiliser le hash
                      window.location.hash = '#contact';
                    }
                  }}
                >
                  Me contacter
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className={`skills ${isPageContentVisible ? 'section-visible' : ''}`}>
        <div className="skills-container">
          <div className="section-header">
            <div className="section-badge">
              <span>Compétences</span>
            </div>
            <h2 className="section-title">
              Technologies & Outils maîtrisés
            </h2>
            <p className="section-description">
              Un éventail complet de technologies modernes pour créer des solutions innovantes
            </p>
          </div>

          <div className="skills-carousel-wrapper">
            <button 
              className="carousel-btn carousel-btn-prev"
              onClick={handlePrev}
              aria-label="Précédent"
            >
              <FaChevronLeft />
            </button>
            
            <div className="skills-carousel">
              <div 
                className="skills-carousel-track"
                style={{
                  transform: getMobileTransform(),
                  transition: isTransitioning ? 'transform 0.8s ease-in-out' : 'transform 0s linear'
                }}
              >
                {/* RENDU DIFFÉRENT SELON MOBILE/DESKTOP */}
                {isMobile ? (
                  // ===== MOBILE : Logique avec clones pour boucle infinie =====
                  <>
                    {/* Clone de la dernière carte au début */}
                    {skills.slice(-1).map((skill, index) => {
                      const IconComponent = skill.icon;
                      return (
                        <div key={`clone-start-${index}`} className="skill-card">
                          <div className="skill-icon" style={{ color: skill.color }}>
                            <IconComponent />
                          </div>
              <div className="skill-content">
                            <h3 className="skill-name">{skill.name}</h3>
                <div className="skill-level">
                  <div className="level-bar">
                                <div 
                                  className="level-fill" 
                                  style={{ 
                                    width: `${skill.level}%`,
                                    backgroundColor: skill.color
                                  }}
                                ></div>
                  </div>
                              <span className="level-percentage">{skill.level}%</span>
                </div>
              </div>
            </div>
                      );
                    })}
                    {/* Cartes principales */}
                    {skills.map((skill, index) => {
                      const IconComponent = skill.icon;
                      return (
                        <div key={index} className="skill-card">
                          <div className="skill-icon" style={{ color: skill.color }}>
                            <IconComponent />
                          </div>
              <div className="skill-content">
                            <h3 className="skill-name">{skill.name}</h3>
                <div className="skill-level">
                  <div className="level-bar">
                                <div 
                                  className="level-fill" 
                                  style={{ 
                                    width: `${skill.level}%`,
                                    backgroundColor: skill.color
                                  }}
                                ></div>
                  </div>
                              <span className="level-percentage">{skill.level}%</span>
                </div>
              </div>
            </div>
                      );
                    })}
                    {/* Clone de la première carte à la fin */}
                    {skills.slice(0, 1).map((skill, index) => {
                      const IconComponent = skill.icon;
                      return (
                        <div key={`clone-end-${index}`} className="skill-card">
                          <div className="skill-icon" style={{ color: skill.color }}>
                            <IconComponent />
                          </div>
              <div className="skill-content">
                            <h3 className="skill-name">{skill.name}</h3>
                <div className="skill-level">
                  <div className="level-bar">
                                <div 
                                  className="level-fill" 
                                  style={{ 
                                    width: `${skill.level}%`,
                                    backgroundColor: skill.color
                                  }}
                                ></div>
                  </div>
                              <span className="level-percentage">{skill.level}%</span>
                </div>
              </div>
            </div>
                      );
                    })}
                  </>
                ) : (
                  // ===== DESKTOP : Logique avec clones (CONSERVÉE) =====
                  <>
                    {/* Dupliquer les dernières cartes au début pour boucle infinie */}
                    {skills.slice(-4).map((skill, index) => {
                      const IconComponent = skill.icon;
                      return (
                        <div key={`clone-start-${index}`} className="skill-card">
                          <div className="skill-icon" style={{ color: skill.color }}>
                            <IconComponent />
                          </div>
              <div className="skill-content">
                            <h3 className="skill-name">{skill.name}</h3>
                <div className="skill-level">
                  <div className="level-bar">
                                <div 
                                  className="level-fill" 
                                  style={{ 
                                    width: `${skill.level}%`,
                                    backgroundColor: skill.color
                                  }}
                                ></div>
                  </div>
                              <span className="level-percentage">{skill.level}%</span>
                </div>
              </div>
            </div>
                      );
                    })}
                    {/* Cartes principales */}
                    {skills.map((skill, index) => {
                      const IconComponent = skill.icon;
                      return (
                        <div key={index} className="skill-card">
                          <div className="skill-icon" style={{ color: skill.color }}>
                            <IconComponent />
                          </div>
              <div className="skill-content">
                            <h3 className="skill-name">{skill.name}</h3>
                <div className="skill-level">
                  <div className="level-bar">
                                <div 
                                  className="level-fill" 
                                  style={{ 
                                    width: `${skill.level}%`,
                                    backgroundColor: skill.color
                                  }}
                                ></div>
                  </div>
                              <span className="level-percentage">{skill.level}%</span>
                </div>
              </div>
            </div>
                      );
                    })}
                    {/* Dupliquer les premières cartes à la fin pour boucle infinie */}
                    {skills.slice(0, 4).map((skill, index) => {
                      const IconComponent = skill.icon;
                      return (
                        <div key={`clone-end-${index}`} className="skill-card">
                          <div className="skill-icon" style={{ color: skill.color }}>
                            <IconComponent />
                          </div>
              <div className="skill-content">
                            <h3 className="skill-name">{skill.name}</h3>
                <div className="skill-level">
                  <div className="level-bar">
                                <div 
                                  className="level-fill" 
                                  style={{ 
                                    width: `${skill.level}%`,
                                    backgroundColor: skill.color
                                  }}
                                ></div>
                  </div>
                              <span className="level-percentage">{skill.level}%</span>
                </div>
              </div>
            </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>

            <button 
              className="carousel-btn carousel-btn-next"
              onClick={handleNext}
              aria-label="Suivant"
            >
              <FaChevronRight />
            </button>
          </div>

          <div className="carousel-indicators">
            {isMobile ? (
              // ===== MOBILE : Un indicateur par carte (index 1 à 14) =====
              skills.map((_, index) => {
                // Sur mobile, currentSkillIndex va de 1 à 14 (après clone)
                // L'indicateur actif correspond à currentSkillIndex - 1
                const cardIndex = index; // 0 à 13
                const activeIndex = currentSkillIndex - 1; // 0 à 13 (currentSkillIndex 1-14)
                return (
                  <button
                    key={index}
                    className={`carousel-indicator ${activeIndex === cardIndex ? 'active' : ''}`}
                    onClick={() => {
                      setIsAutoPlaying(false);
                      setIsTransitioning(true);
                      setCurrentSkillIndex(index + 1); // +1 car index commence à 1 sur mobile
                      setTimeout(() => setIsTransitioning(false), 800);
                    }}
                    aria-label={`Aller à la slide ${index + 1}`}
                  />
                );
              })
            ) : (
              // ===== DESKTOP : Logique existante (CONSERVÉE) =====
              (() => {
                // Sur desktop : on voit 4 cartes à la fois
                // Avec 14 cartes, on peut avoir 11 positions différentes (14 - 4 + 1 = 11)
                // Formule : skills.length - cardsVisible + 1
                const cardsVisible = 4;
                const startIndex = cardsVisible; // 4 (après les clones)
                const maxIndicatorCount = Math.max(1, skills.length - cardsVisible + 1);
                
                return Array.from({ length: maxIndicatorCount }).map((_, index) => {
                  const maxRealIndex = startIndex + (skills.length - cardsVisible);
                  let actualIndex = 0;
                  
                  if (currentSkillIndex <= startIndex) {
                    actualIndex = 0;
                  } else if (currentSkillIndex > maxRealIndex) {
                    actualIndex = maxIndicatorCount - 1; // Dernier indicateur
                  } else {
                    actualIndex = currentSkillIndex - startIndex;
                  }
                  
                  actualIndex = Math.max(0, Math.min(actualIndex, maxIndicatorCount - 1));
                  
                  return (
                    <button
                      key={index}
                      className={`carousel-indicator ${actualIndex === index ? 'active' : ''}`}
                      onClick={() => {
                        setIsAutoPlaying(false);
                        setIsTransitioning(true);
                        setCurrentSkillIndex(index + startIndex);
                        setTimeout(() => setIsTransitioning(false), 800);
                      }}
                      aria-label={`Aller à la slide ${index + 1}`}
                    />
                  );
                });
              })()
            )}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className={`projects ${isPageContentVisible ? 'section-visible' : ''}`}>
        <div className="projects-container">
          <div className="section-header">
            <div className="section-badge">
              <span>Projets</span>
            </div>
            <h2 className="section-title">
              Mes réalisations récentes
            </h2>
            <p className="section-description">
              Une sélection de projets qui démontrent mes compétences et ma passion pour l'innovation
            </p>
          </div>

          <div className="projects-grid">
            <a
              href="https://ulix-studio.com"
              target="_blank"
              rel="noopener noreferrer"
              className="project-card featured"
            >
              <div className="project-image">
                <img className="project-thumb" src="/thumbs/pf_ulix-studio.jpg" alt="Aperçu du site Ulix Studio" loading="lazy" />
                <div className="image-overlay"></div>
                <div className="featured-badge">
                  <span><FaRocket /> Projet phare</span>
                </div>
                <span className="project-visit">Voir le site <FaArrowRight /></span>
              </div>
              <div className="project-content">
                <h3 className="project-title">Ulix Studio</h3>
                <p className="project-description">
                  Plateforme SaaS complète : tableau de bord client, chiffrement, gestion de licences, authentification et monitoring temps réel.
                </p>
                <div className="project-technologies">
                  <span className="tech-tag">Next.js</span>
                  <span className="tech-tag">React</span>
                  <span className="tech-tag">Node.js</span>
                  <span className="tech-tag">MySQL</span>
                </div>
              </div>
            </a>

            <a
              href="https://aquavantys.mrtchoukdev.com"
              target="_blank"
              rel="noopener noreferrer"
              className="project-card"
            >
              <div className="project-image">
                <img className="project-thumb" src="/thumbs/pf_aquavantys.jpg" alt="Aperçu du site Aquavantys" loading="lazy" />
                <div className="image-overlay"></div>
                <span className="project-visit">Voir le site <FaArrowRight /></span>
              </div>
              <div className="project-content">
                <h3 className="project-title">Aquavantys</h3>
                <p className="project-description">
                  Plateforme de location et de vente de yachts haut de gamme. Catalogue filtrable, réservation en ligne et journal éditorial.
                </p>
                <div className="project-technologies">
                  <span className="tech-tag">Next.js</span>
                  <span className="tech-tag">React</span>
                  <span className="tech-tag">SEO</span>
                  <span className="tech-tag">i18n</span>
                </div>
              </div>
            </a>

            <a
              href="https://agency.mrtchoukdev.com"
              target="_blank"
              rel="noopener noreferrer"
              className="project-card"
            >
              <div className="project-image">
                <img className="project-thumb" src="/thumbs/pf_agency.jpg" alt="Aperçu du site Ulix Agency" loading="lazy" />
                <div className="image-overlay"></div>
                <span className="project-visit">Voir le site <FaArrowRight /></span>
              </div>
              <div className="project-content">
                <h3 className="project-title">Ulix Agency</h3>
                <p className="project-description">
                  Site d'agence web premium : animations sur-mesure, prise de rendez-vous sécurisée et présentation des réalisations.
                </p>
                <div className="project-technologies">
                  <span className="tech-tag">Next.js</span>
                  <span className="tech-tag">Framer Motion</span>
                  <span className="tech-tag">Tailwind</span>
                  <span className="tech-tag">SEO</span>
                </div>
              </div>
            </a>
          </div>

          {/* Bouton "Voir plus de réalisations" */}
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <Link 
              to="/projects"
              className="btn btn-secondary"
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                padding: '1rem 2rem',
                fontSize: '1rem',
                fontWeight: '600',
                textDecoration: 'none'
              }}
            >
              <span>Voir plus de réalisations</span>
              <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className={`contact ${isPageContentVisible ? 'section-visible' : ''}`}>
        <div className="contact-container">
          <div className="section-header">
            <div className="section-badge">
              <span>Contact</span>
            </div>
            <h2 className="section-title">
              Travaillons ensemble
            </h2>
            <p className="section-description">
              Prêt à donner vie à votre prochain projet ? Contactez-moi pour discuter de vos besoins
            </p>
          </div>

          <div className="contact-content">
            <div className="contact-info">
              <div className="info-cards">
                <div className="info-card">
                  <div className="info-icon"><FaEnvelope /></div>
                  <div className="info-content">
                    <h3>Email</h3>
                    <a href="mailto:contact@mrtchoukdev.com">contact@mrtchoukdev.com</a>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-icon"><FaPhone /></div>
                  <div className="info-content">
                    <h3>Téléphone</h3>
                    <a href="tel:+33612345678">+33 6 12 34 56 78</a>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-icon"><FaMapMarkerAlt /></div>
                  <div className="info-content">
                    <h3>Localisation</h3>
                    <span>Paris, France</span>
                  </div>
                </div>
              </div>

              <div className="social-links">
                <h3>Suivez-moi</h3>
                <div className="social-icons">
                  <button
                    className="social-link"
                    onClick={(e) => {
                      e.preventDefault();
                      e.currentTarget.blur();
                      setGithubNotificationContact({ 
                        show: true, 
                        closing: false,
                        message: 'Le GitHub n\'est pas encore prêt, veuillez patienter.' 
                      });
                      setTimeout(() => {
                        setGithubNotificationContact(prev => ({ ...prev, closing: true }));
                        setTimeout(() => {
                          setGithubNotificationContact({ show: false, closing: false, message: '' });
                        }, 600);
                      }, 8000);
                    }}
                  >
                    <FaGithub /> GitHub
                  </button>
                  <button
                    className="social-link"
                    onClick={(e) => {
                      e.preventDefault();
                      e.currentTarget.blur();
                      setLinkedinNotificationContact({ 
                        show: true, 
                        closing: false,
                        message: 'LinkedIn n\'est pas encore prêt, veuillez patienter, il sera disponible prochainement.' 
                      });
                      setTimeout(() => {
                        setLinkedinNotificationContact(prev => ({ ...prev, closing: true }));
                        setTimeout(() => {
                          setLinkedinNotificationContact({ show: false, closing: false, message: '' });
                        }, 600);
                      }, 8000);
                    }}
                  >
                    <FaLinkedin /> LinkedIn
                  </button>
                  <button
                    className="social-link"
                    onClick={(e) => {
                      e.preventDefault();
                      e.currentTarget.blur();
                      setTwitterNotificationContact({ 
                        show: true, 
                        closing: false,
                        message: 'Le compte Twitter est en cours de préparation, veuillez patienter, merci.' 
                      });
                      setTimeout(() => {
                        setTwitterNotificationContact(prev => ({ ...prev, closing: true }));
                        setTimeout(() => {
                          setTwitterNotificationContact({ show: false, closing: false, message: '' });
                        }, 600);
                      }, 8000);
                    }}
                  >
                    <FaTwitter /> Twitter
                  </button>
                </div>
                
                {/* Notification pour GitHub dans la section contact */}
                {githubNotificationContact.show && (
                  <div className={`form-status warning cv-notification ${githubNotificationContact.closing ? 'closing' : ''}`} style={{ 
                    marginTop: '1.5rem'
                  }}>
                    <div className="form-status-content">
                      <div className="status-icon" style={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: 'white',
                        boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
                      }}>
                        <FaGithub />
                      </div>
                      <div className="status-message">
                        {githubNotificationContact.message}
                      </div>
                    </div>
                  </div>
                )}

                {/* Notification pour LinkedIn dans la section contact */}
                {linkedinNotificationContact.show && (
                  <div className={`form-status warning cv-notification ${linkedinNotificationContact.closing ? 'closing' : ''}`} style={{ 
                    marginTop: '1.5rem'
                  }}>
                    <div className="form-status-content">
                      <div className="status-icon" style={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: 'white',
                        boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
                      }}>
                        <FaLinkedin />
                      </div>
                      <div className="status-message">
                        {linkedinNotificationContact.message}
                      </div>
                    </div>
                  </div>
                )}

                {/* Notification pour Twitter dans la section contact */}
                {twitterNotificationContact.show && (
                  <div className={`form-status warning cv-notification ${twitterNotificationContact.closing ? 'closing' : ''}`} style={{ 
                    marginTop: '1.5rem'
                  }}>
                    <div className="form-status-content">
                      <div className="status-icon" style={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: 'white',
                        boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
                      }}>
                        <FaTwitter />
                      </div>
                      <div className="status-message">
                        {twitterNotificationContact.message}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="contact-form">
              <form className="form" onSubmit={handleContactSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Nom complet</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    placeholder="Votre nom complet"
                    required
                    disabled={isSubmittingContact}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    placeholder="votre@email.com"
                    required
                    disabled={isSubmittingContact}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Sujet</label>
                  <input 
                    type="text" 
                    id="subject" 
                    name="subject" 
                    placeholder="Sujet de votre message"
                    required
                    disabled={isSubmittingContact}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows="6"
                    placeholder="Décrivez votre projet ou votre demande..."
                    required
                    disabled={isSubmittingContact}
                  ></textarea>
                </div>

                {/* Message de statut */}
                {contactFormStatus.message && (
                  <div className={`form-status ${contactFormStatus.type === 'success' ? 'success' : 'error'} cv-notification ${contactFormStatus.closing ? 'closing' : ''}`}>
                    <div className="form-status-content">
                      {contactFormStatus.type === 'success' ? (
                        <div className="status-icon success-icon">
                          <FaCheck />
                        </div>
                      ) : (
                        <div className="status-icon error-icon">
                          <FaTimes />
                        </div>
                      )}
                      <div className="status-message">
                        {contactFormStatus.message}
                      </div>
                    </div>
                  </div>
                )}

                <button 
                  type="submit" 
                  className="btn btn-primary submit-btn"
                  disabled={isSubmittingContact}
                >
                  <span>
                    {isSubmittingContact ? (
                      <>⏳ Envoi en cours...</>
                    ) : (
                      <><FaEnvelope /> Envoyer le message</>
                    )}
                  </span>
                </button>
              </form>
            </div>
          </div>

          <div className="contact-footer">
            <div className="availability">
              <div className="status-indicator">
                <div className="status-dot"></div>
                <span>Disponible pour nouveaux projets</span>
              </div>
              <p>Réponse sous 24h en moyenne</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`footer ${isPageContentVisible ? 'section-visible' : ''}`}>
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-main">
              <div className="footer-brand">
                <h3>Mrtchouk</h3>
                <p>Développeur Full Stack passionné par l'innovation technologique</p>
              </div>

              <div className="footer-links">
                <div className="link-group">
                  <h4>Navigation</h4>
                  <ul>
                    <li><a href="#home">Accueil</a></li>
                    <li><a href="#about">À propos</a></li>
                    <li><a href="#skills">Compétences</a></li>
                    <li><a href="#projects">Projets</a></li>
                  </ul>
                </div>

                <div className="link-group">
                  <h4>Technologies</h4>
                  <ul>
                    <li>React & Frontend</li>
                    <li>Python & Backend</li>
                    <li>LUA & Scripting</li>
                    <li>DevOps & Docker</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="footer-bottom">
              <div className="footer-copyright">
                <p>
                  © {new Date().getFullYear()} Mrtchouk. Fait avec <FaHeart /> par un développeur passionné
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Color Picker Modal */}
      {isColorModalOpen && (
        <div className={`modal-overlay ${isModalClosing ? 'closing' : ''}`} onClick={closeModal}>
          <div className={`color-modal ${isModalClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Choisir une couleur</h3>
              <button 
                className="modal-close"
                onClick={closeModal}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="color-grid">
                {colors.map((color, index) => (
                  <button
                    key={`${color}-${index}`}
                    className={`color-option ${accentColor === color ? 'active' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setAccentColor(color)}
                    title={`Couleur ${color}`}
                  >
                    {accentColor === color && <span className="checkmark"><FaCheck /></span>}
                  </button>
                ))}
              </div>
              
              <div className="color-preview">
                <div className="preview-title">Aperçu</div>
                <div className="preview-elements">
                  <div className="preview-button" style={{ backgroundColor: accentColor }}>
                    Bouton
                  </div>
                  <div className="preview-text" style={{ color: accentColor }}>
                    Texte accent
                  </div>
                  <div className="preview-gradient" style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)` }}>
                    Gradient
                  </div>
                </div>
              </div>
              
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={closeModal}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Garde de route côté client.
 *
 * Confort d'affichage uniquement : elle évite de montrer l'interface d'admin à
 * un visiteur, mais ne protège rien. La vraie autorisation est appliquée par
 * l'API, qui refuse toute écriture sans session valide.
 */
const ProtectedAdmin = () => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      // C'est le serveur qui répond, pas un indicateur localStorage.
      const isAuthenticated = await verifySession();

      if (cancelled) {
        return;
      }

      if (!isAuthenticated) {
        navigate('/login');
      } else {
        setIsChecking(false);
      }
    };

    checkAuth();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="app" style={{ minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: '1.5rem' }}>Vérification...</div>
      </div>
    );
  }
  
  console.log('🔐 ProtectedAdmin: Rendering Admin component');
  return <Admin />;
};

function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/projects" element={<AllProjects />} />
      <Route path="/login" element={<Login />} />
      <Route path="/admin" element={<ProtectedAdmin />} />
    </Routes>
  );
}

export default App;